/**
 * Asking for signed media URLs, in requests that actually fit in a URL.
 *
 * `media.urls` is a query, so its input travels in the query string. That makes
 * the number of file ids a *transport* limit, not a server one — and it is a
 * limit nobody thinks about while writing a call site, because it only bites in
 * a library big enough to exceed it.
 *
 * It bit hard. The gallery asked for a page of a hundred at a time, which is a
 * 3,800-character URL against a 2,048 limit, so the request failed outright and
 * every tile on the page lost its thumbnail at once. The photographs were
 * perfectly fine; the group looked destroyed. Small groups never showed it,
 * which made it look like corruption from a bad upload rather than arithmetic.
 *
 * Four other call sites had the same shape — People, the slideshow, save-to-
 * phone, album covers — each with its own hand-picked batch size, each one
 * wrong at scale. Hand-tuning them separately is how this happened, so the
 * bound lives here instead and every caller goes through it.
 *
 *     20 ids ≈   880 characters
 *     50 ids ≈ 1,930 characters
 *    100 ids ≈ 3,680 characters   ← over the limit
 */

import { trpcClient } from "./trpc.js";

/**
 * Twenty, measured rather than guessed.
 *
 * Small enough that several of these can still be combined by the batching
 * layer and stay inside the limit, which is where the remaining efficiency
 * comes from — the chunking costs round trips only when the batcher cannot
 * merge them.
 */
export const URL_BATCH = 20;

type Variant = "thumb" | "preview" | "preview_hq" | "original";

export interface SignedUrlEntry {
  fileId: string;
  url: string | null;
  wrappedFileKey: string | null;
  width?: number | null;
  height?: number | null;
  format?: string;
  reason?: string;
}

/**
 * Sign every id given, however many there are.
 *
 * Chunks run in sequence rather than all at once: a library of ten thousand
 * would otherwise open five hundred simultaneous requests and trip the rate
 * limiter, which is its own way of turning a whole gallery grey.
 */
export async function fetchSignedUrls(
  groupId: string,
  fileIds: readonly string[],
  variant: Variant,
  accept?: string,
): Promise<SignedUrlEntry[]> {
  const entries: SignedUrlEntry[] = [];

  /*
   * Originals are signed one at a time, because the batch procedure refuses
   * them outright — “Original files must be requested one at a time” — and each
   * one is audited and metered separately.
   *
   * Saving to a phone at full quality asked the batch endpoint for originals
   * and was refused every time, so that path had never worked. Doing what the
   * server asks for is the fix.
   */
  if (variant === "original") {
    for (const fileId of fileIds) {
      const one = await trpcClient.media.url.query({
        groupId,
        fileId,
        variant: "original",
        intent: "download",
      });
      entries.push({
        fileId,
        url: one.url ?? null,
        wrappedFileKey: one.wrappedFileKey ?? null,
      });
    }
    return entries;
  }

  for (let start = 0; start < fileIds.length; start += URL_BATCH) {
    const batch = fileIds.slice(start, start + URL_BATCH);
    if (batch.length === 0) continue;

    const result = await trpcClient.media.urls.query({
      groupId,
      fileIds: batch,
      variant,
      ...(accept ? { accept } : {}),
    });
    entries.push(...(result.urls as SignedUrlEntry[]));
  }

  return entries;
}
